import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ShieldAlert } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { EmptyState } from '../components/guidance/empty-state';
import { Tip } from '../components/guidance/tip';
import { type TourDef, useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { useConfirm } from '../components/ui/confirm';
import { BatchSettings } from '../components/upload/batch-settings';
import { DropZone } from '../components/upload/drop-zone';
import { RecentUploads } from '../components/upload/recent-uploads';
import { UploadList } from '../components/upload/upload-list';
import { useCan } from '../lib/session';
import type { PickedFile } from '../lib/uploads/collect';
import { isFinal, UploadEngine } from '../lib/uploads/engine';
import { takeHandedOffFiles } from '../lib/uploads/handoff';

export const Route = createFileRoute('/upload')({ component: UploadPage });

const UPLOAD_TOUR: TourDef = {
  id: 'upload',
  steps: [
    {
      target: '[data-testid="drop-zone"]',
      title: 'Add files or folders',
      body: 'Drop anything: documents, sheets, transcripts, images, audio, video. Folders keep their structure as collections.',
    },
    {
      target: '[data-tour="batch-settings"]',
      title: 'Settings for the whole batch',
      body: 'Sensitivity and review apply to every file; each file can override them in the list.',
      learnMore: 'sensitivity-and-ai-routing',
    },
    {
      title: 'Pause, resume, cancel',
      body: 'Large files upload in parts and resume after a lost connection. Cancel works at every stage and leaves nothing behind.',
      learnMore: 'upload-a-batch',
    },
  ],
};

function UploadPage() {
  useScreenTour(UPLOAD_TOUR);
  const canUpload = useCan('content.write');
  const qc = useQueryClient();
  const confirm = useConfirm();
  const engine = useMemo(() => new UploadEngine(undefined, () => qc.invalidateQueries({ queryKey: ['uploads'] })), [qc]);
  const items = useSyncExternalStore(engine.subscribe, engine.getSnapshot);
  const [settings, setSettings] = useState(engine.settings);
  const updateSettings = (s: typeof settings) => {
    engine.settings = s;
    setSettings(s);
  };
  const add = useCallback((files: PickedFile[]) => void engine.add(files), [engine]);
  // Files dropped on Home arrive here, ready to check and start like any other.
  useEffect(() => {
    const handed = takeHandedOffFiles();
    if (handed.length) void engine.add(handed);
  }, [engine]);

  const ready = items.filter(i => i.state === 'ready').length;
  const checking = items.some(i => i.state === 'checking');
  const running = items.some(i => ['queued', 'uploading', 'paused', 'finishing'].includes(i.state));
  const cancellable = items.some(i => !isFinal(i));

  const cancelAll = async () => {
    if ((await engine.cancelAll()) === 'confirm') {
      const ok = await confirm({
        title: 'Cancel every file in this batch?',
        body: 'AI work has already been done on some of these files. Everything produced from them will be deleted.',
        confirmLabel: 'Cancel all',
        destructive: true,
      });
      if (ok) await engine.cancelAll(true);
    }
    qc.invalidateQueries({ queryKey: ['uploads'] });
  };

  if (!canUpload)
    return (
      <>
        <PageHeader title="Upload" />
        <EmptyState icon={ShieldAlert} title="Uploading needs an editor role" learnMore="upload-a-batch">
          Viewers can read everything but can't add sources. Ask an admin if you need to upload.
        </EmptyState>
      </>
    );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Upload"
        description="Every file goes into the Library; Hoopoe then extracts actors, relations and events for review."
      />
      <DropZone onFiles={add} />
      <Tip id="upload-markings" learnMore="sensitivity-and-ai-routing">
        Marked documents (<em>CONFIDENTIAL</em>, <em>Restricted</em>, <bdi dir="auto">سري</bdi>…) are held until you say whether they're
        confidential. Nothing goes to an external model before that.
      </Tip>
      <BatchSettings value={settings} onChange={updateSettings} disabled={running} />
      {items.length > 0 && (
        <section aria-labelledby="batch-title" className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 id="batch-title" className="font-semibold">
              Files <span className="tabular text-muted-foreground">({items.length})</span>
            </h2>
            <div className="flex flex-wrap gap-2">
              <Button variant="ghost" size="sm" onClick={() => engine.clearFinished()} disabled={!items.some(isFinal)}>
                Clear finished
              </Button>
              <Button variant="outline" size="sm" onClick={cancelAll} disabled={!cancellable}>
                Cancel all
              </Button>
              <Button size="sm" onClick={() => engine.start()} disabled={!ready || checking}>
                {checking ? 'Checking files…' : `Start upload${ready ? ` (${ready} file${ready === 1 ? '' : 's'})` : ''}`}
              </Button>
            </div>
          </div>
          <UploadList items={items} engine={engine} batchSensitivity={settings.sensitivity} />
        </section>
      )}
      <RecentUploads />
    </div>
  );
}
